// @flow
import * as React from 'react'
import Button from '../Button'
import Photographer from '../Photographer'
import MouseStates from '../../utils/MouseStates'
import Dot from './Dot'
import SocialNetwork from '../SocialNetwork'
import { parseDate, stringToHTML, removePonctuationAfterTag } from '../../utils/parsingUtils'
import DetailAnimation from '../../utils/detailAnimationsUtils'

import logoAfp from '../../assets/images/logo_afp.svg'

import './styles.css'
import '../Button/styles.css'

import type PhotographersApp  from '../../utils/photographersApp'
import type { DailyItemType, ArticleType } from 'database-helper'
import type { DivRefType, HeadingRefType } from 'react-helper'
import type { ContextRouter } from 'react-router-dom'

type PropsType = {
  ...ContextRouter,
  dailyItem: ?DailyItemType,
  PhotographersApp: PhotographersApp,
  returnToArticle: () => void,
  MouseStates: MouseStates
}

type StateType = {
  article: ?ArticleType,
  detailOpacity: number,
  elementsOpacity: number,
  canTriggerOpacity: boolean,
  DetailAnimation: DetailAnimation
}

export default class Detail extends React.Component<PropsType, StateType> {
  rafID: ?AnimationFrameID
  detailContainerRef: DivRefType
  backButtonContainerRef: HeadingRefType
  quitBtnOpacity: number
  quitBtnPEvents: string
  scrollIndicationOpacity: number
  stopMouseEvents: boolean
  constructor(props: PropsType) {
    super(props)
    this.detailContainerRef = React.createRef()
    this.backButtonContainerRef = React.createRef()
    this.quitBtnOpacity = 0;
    this.quitBtnPEvents = 'none';
    this.scrollIndicationOpacity = 1;
    this.stopMouseEvents = false;

    const elementsOpacity = 0
    this.state = {
      article: undefined,
      detailOpacity: 0,
      elementsOpacity: elementsOpacity,
      canTriggerOpacity: true,
      DetailAnimation: new DetailAnimation(elementsOpacity, this.setElementsOpacity),
      reveal: false,
    }
  }

  componentDidMount() {
    const containerRef = this.detailContainerRef.current
    this.props.MouseStates.setShowArticle(true);
    if (containerRef) {
      setTimeout(() => {
        containerRef.classList.add('detail-container-visible')
        this.state.DetailAnimation.firstPlay()
          .then(() => {
            containerRef.addEventListener('mousemove', this.mousemove)
            const backBtnContainer = this.backButtonContainerRef.current
            if (backBtnContainer) backBtnContainer.classList.add('back-btn-visible')
          })
      }, 100)
    }
  }

  componentWillUnmount() {
    this.state.DetailAnimation.destroy()
    this.props.MouseStates.setShowArticle(false);
    const containerRef = this.detailContainerRef.current
    if (containerRef) {
      containerRef.removeEventListener('mousemove', this.mousemove)
    }
  }

  setElementsOpacity = (elementsOpacity: number) => {
    this.setState({ elementsOpacity })
  }

  mousemove = () => {
    if(!this.stopMouseEvents) {
      if (this.state.canTriggerOpacity || this.props.MouseStates.getHoverState()) this.makeElementsAppear(false)
    }
  }

  makeElementsAppear = (force: boolean = false) => {
    if (!this.state.article || force) this.state.DetailAnimation.startAppear()
  }

  getDailyItemBackground = (dailyItem: DailyItemType, opacity: number = 1): string => (
    `rgba(${dailyItem.color.r}, ${dailyItem.color.g}, ${dailyItem.color.b}, ${opacity})`
  )

  getTransparentBg = (dailyItem: DailyItemType, article: ?ArticleType): string => {
    const alpha = article ? 0.8 : 0
    return `rgba(${dailyItem.color.r}, ${dailyItem.color.g}, ${dailyItem.color.b}, ${alpha})`
  }

  closeArticle = () => {
    if (this.props.MouseStates.showArticle) {
      this.setState({ article: undefined, detailOpacity: 0 })
      this.makeElementsAppear(true)
      this.props.MouseStates.disabledDetailPage();
      // this.props.MouseStates.setShowArticle(false); 
      this.quitBtnOpacity = 0;
      this.quitBtnPEvents = 'none';
      this.scrollIndicationOpacity = 1;  
    }
  }

  setArticleOpacities = (e: WheelEvent) => {
    e.preventDefault()
    const amplitude = e.deltaY < 0 ? -0.05 : 0.05
    this.setState({ detailOpacity: this.state.detailOpacity + amplitude })
    this.quitBtnOpacity = 1;
    this.quitBtnPEvents = 'all';
    this.scrollIndicationOpacity = 0;
  }

  onWheel = (e: WheelEvent) => {
    if (this.state.detailOpacity < 1) this.setArticleOpacities(e)
  }

  pauseOpacity = () => {
    if (this.state.article) return undefined
    this.setState({ canTriggerOpacity: false })
    this.state.DetailAnimation.pauseAppear()
  }

  unpauseOpacity = () => {
    this.state.DetailAnimation.unpauseAppear()
    this.setState({ canTriggerOpacity: true })
  }

  articleClicked = (article: ArticleType) => {
    if (!this.state.article) {
      this.unpauseOpacity()
      this.setState({ article })
      this.props.MouseStates.setShowArticle(true);
    }
  }

  getHistoryState = (): { articleUID: string } | null => {
    const dailyItem = this.props.dailyItem
    if (dailyItem) return { articleUID: dailyItem.uid }
    return null
  }

  closeTransition() {
    const backButtonContainerRef = this.backButtonContainerRef.current
    const detailContainerRef = this.detailContainerRef.current
    if (!backButtonContainerRef || !detailContainerRef) return undefined
    this.setState({canTriggerOpacity: false})
    this.stopMouseEvents = true;
    backButtonContainerRef.classList.remove('back-btn-visible');    
    
    setTimeout(() => {
      if (this.detailContainerRef.current && this.detailContainerRef.current.classList.contains('detail-container-visible')) this.detailContainerRef.current.classList.remove('detail-container-visible');
      setTimeout(() => {
        this.props.returnToArticle()
      }, 500);
    }, 700)
  }

  render404 = () => {
    return (
      <div>
        <h1>NOT FOUND SORRY</h1>
        <h1 onClick={() => this.props.returnToArticle()}>BACK</h1>
      </div>
    )
  }

  renderContent = (contents: Array<string>, type: 'preview' | 'full') => {
    if (type === 'preview') {
      const toParse = contents[0].substring(0, 100)
      const parsed = toParse.split(' ')
      const str = contents[0].length > 100 ? parsed.slice(0, parsed.length - 1).join(' ') : contents[0]

      const needsBracket = contents[0].length > 100 || contents[1]
      
      const articleWithoutBrackets = needsBracket ? stringToHTML(`${str} [...]`, /"/, ['<i>', '</i>']) : stringToHTML(str, /"/, ['<i>', '</i>'])
      const formattedArticle = stringToHTML(removePonctuationAfterTag(articleWithoutBrackets), /\+/, ['"', '"'])
      return <div><h2 dangerouslySetInnerHTML={{__html: formattedArticle}} /></div>
    } else {
      return contents.map((content: string, index: number) => {
        const articleWithoutBrackets = stringToHTML(content, /"/, ['<i>', '</i>'])
        const formattedArticle = stringToHTML(removePonctuationAfterTag(articleWithoutBrackets), /\+/, ['"', '"'])
        return (
          <div key={`${content.substring(0, 5)}${String(index)}}`}>
            <p dangerouslySetInnerHTML={{__html: formattedArticle}} />
          </div>
        )
      })
    }
  }

  renderArticle = (dailyItem: ?DailyItemType, article: ?ArticleType) => {
    if (!dailyItem || !article) return null
    const parsedDate = parseDate(article.date)
    return (
      <div
        className="detail-article-container-show"
        style={{ backgroundColor: this.getDailyItemBackground(dailyItem, this.state.detailOpacity) }}
        onWheel={(e) => this.onWheel(e)}
      >
        <div style={{ opacity: this.quitBtnOpacity, pointerEvents: this.quitBtnPEvents }} className="btn-container quit-btn" onClick={() => this.closeArticle()}></div>

        <div className="text-container">
          <img src={logoAfp} alt="logo" className="logo"/>
          <p>{parsedDate.formattedMonth} {parsedDate.day}, {parsedDate.year}</p>
          <h2 className="title">{article.title}</h2>
          <div className="detail-article-preview-content-container">
            <div className="preview" style={{ opacity: 1 - this.state.detailOpacity }}>
              {this.renderContent(article.content, 'preview')}
              {this.renderScrollIndication()}
            </div>
            <div className="content" style={{ opacity: this.state.detailOpacity }}>
              {this.renderContent(article.content, 'full')}
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderScrollIndication = () => {
    return (
      <div style={{ opacity: this.scrollIndicationOpacity }} className="scroll-indication">
        <div className="dots-indication">
          <span className="dot-indication"></span>
          <span className="dot-indication"></span>
          <span className="dot-indication"></span>
        </div>
        Scroll to read more
      </div>
    )
  }

  renderDetailPoints = (articles: Array<ArticleType>): Array<React.Node> => (
    articles.map((article: ArticleType) => (
      <div
        className="detail-point"
        onMouseEnter={this.pauseOpacity}
        onMouseLeave={this.unpauseOpacity}
        style={{
          top: article.position.y,
          left: article.position.x,
          opacity: this.state.elementsOpacity
        }}
        key={`${article.position.y}${article.position.x}`}
        onClick={() => { 
            this.articleClicked(article)
        }}
        data-currenttitle={article.title}
      >
        <Dot 
          currentTitle={article.title}
          MouseStates={this.props.MouseStates}
        />
      </div>
    ))
  )

  togglePhotographer = (bool) => {
    this.setState({'reveal': bool});
  }


  render() {
    const dailyItem = this.props.dailyItem
    if (!dailyItem) return this.render404()
    const photographer = this.props.PhotographersApp.getPhotographeThroughId(dailyItem.photographer)
    return (
      <div
        className="detail-container"
        ref={this.detailContainerRef}
        style={{
          backgroundColor: this.getTransparentBg(dailyItem, this.state.article),
          borderColor: this.getDailyItemBackground(dailyItem)
        }}
      >
        {this.renderDetailPoints(dailyItem.articles)}
        <div className="detail-info-container" onClick={() => this.closeArticle()}>
          <h3
            onClick={() => this.closeTransition()}
            ref={this.backButtonContainerRef}
            className="back-btn"
          >
            BACK
          </h3>
          <div
            style={{ opacity: this.state.elementsOpacity }}
            onMouseEnter={this.pauseOpacity}
            onMouseLeave={this.unpauseOpacity}
            className="information-wrapper"
          >
            <div className="location-and-networks">
              <h4 className="information-location">{dailyItem.location}</h4>
              <SocialNetwork type="facebook" url="http://facebook.com" />
              <SocialNetwork type="twitter" url="http://twitter.com" />
            </div>
            <h1 className="information-title">{dailyItem.title}</h1>
            <Button action={() => { this.togglePhotographer(true) }}>{photographer.name}</Button>
          </div>
        </div>
        <div className="detail-article-container">
          {this.renderArticle(this.props.dailyItem, this.state.article)}
        </div>


        <Photographer reveal={this.state.reveal} trigger={this.togglePhotographer}></Photographer>
        
      </div>
    )
  }
}

//            <Button action={() => { this.props.history.push(`/photographer/${dailyItem.photographer}`, this.getHistoryState()) }}>{photographer.name}</Button>



// WEBPACK FOOTER //
// ./src/components/Detail/index.jsx